
    import type { Emitter } from 'nanoevents'
    import { AltApplication } from '@repo/alt/package'
    import DT from '@repo/alt/DependencyTypes'
    import type { useRoute, useRouter } from 'vue-router'
    import type { NuxtApp } from 'nuxt/app'
    import type { RuntimeConfig } from '@nuxt/schema';
    import { defineNuxtPlugin } from 'nuxt/app'
    import type { useCookie, refreshCookie, useHead } from 'nuxt/app'
    import type { Pinia } from 'pinia'

    const interopDefault = (promise: Promise<any>): any => {
      return promise.then(m => m.default || m)
    }

    export default defineNuxtPlugin({
      name: 'alt-container:plugin',
      dependsOn: [
        'eventbus-provider:plugin',
        'cookies-provider:plugin',
        'pinia',
      ],
      async setup (context) {
        const nuxtApp = context as NuxtApp
        const $alt = new AltApplication()

        const nuxtDiConfig = {
          setup (ioc: any) {
            ioc.bind<Emitter>(DT.EventBus).toConstantValue(nuxtApp.$eventBus)
            ioc.bind<typeof useCookie>(DT.Cookies).toConstantValue(nuxtApp.$cookies)
            ioc.bind<typeof refreshCookie>(DT.RefreshCookies).toConstantValue(nuxtApp.$refreshCookie)
            ioc.bind<ReturnType<typeof useRoute>>(DT.Route).toConstantValue(nuxtApp.$route)
            ioc.bind<ReturnType<typeof useRouter>>(DT.Router).toConstantValue(nuxtApp.$router)
            ioc.bind<RuntimeConfig>(DT.RuntimeConfig).toConstantValue(nuxtApp.$config)
            ioc.bind<Pinia>(DT.Pinia).toConstantValue(nuxtApp.$pinia)
          }
        }

        // import generated providers/configs module (module will replace this file during build)
        const statisProviders = await interopDefault(import('#alt-di-providers'))

        // Support multiple export shapes:
        // - module exports an array directly
        // - module exports { providers: [...] } or { configs: [...] }
        // - module exports default = array or default = { providers: [...] }
        let configs: any[] = []
        if (Array.isArray(statisProviders)) {
          configs = statisProviders
        } else if (Array.isArray(statisProviders.default)) {
          configs = statisProviders.default
        } else {
          configs = statisProviders.providers || statisProviders.configs || statisProviders.default || []
        }

        // Register DI configs: first our nuxt runtime bindings, then generated configs
        $alt.registerDiConfigs(nuxtDiConfig, ...configs)

        await $alt.boot()

        return {
          provide: {
            alt: $alt
          }
        }
      }
    })
  